/**
 * SIL-1350 - the 4-step buyer-verification modal (600px): Entity, Ownership and
 * authority, Mobile, Agreement and review. Spec:
 * docs/superpowers/specs/2026-09-28-exchange-buyer-ui-design.md section 4.5;
 * field lists and Continue-enable rules follow
 * docs/exchange/design-handoff/README.md section 5.
 *
 * SIL-1346 shipped this file as a placeholder with a settled prop contract, and
 * that contract is unchanged here: the gate passes only `reason` and `onClose`,
 * and everything else - status, draft, `canEdit`, the rejection reason - comes
 * from `useBuyerStatus()`. The gate has no business knowing the form's data
 * requirements.
 *
 * Two deliberate departures from the SIL-1313 prototype, both from spec 4.5:
 * step 4 accepts the **Exchange User Agreement** (linked, versioned) rather
 * than a "Mutual NDA", and `legalName`/`signatoryName` arrive prefilled.
 *
 * Three things worth knowing before editing:
 *
 *  1. **The prefill is a write.** The API only seeds `legal_name` from
 *     `customer_org` and `signatory_name` from the signed-in user inside
 *     `ensureDraft`, which runs on a WRITE. `GET /` on an org that has never
 *     saved anything returns them empty. So when the record looks unseeded the
 *     modal sends an empty `PUT /draft` on open - the route runs `ensureDraft`,
 *     skips the UPDATE (empty patch) and returns the seeded record. One
 *     round trip, no new endpoint, and the prefill is the server's value rather
 *     than a second client-side guess at the org's legal name.
 *  2. **Form state is seeded exactly once** (`seeded`). Verifying the SMS code
 *     and uploading a document both invalidate the shared status cache so the
 *     rest of the app sees the change, and a refetch landing mid-form must not
 *     reset what the user has typed.
 *  3. **The resend countdown is derived from the server's `resendAvailableAt`,
 *     never a local 30s timer** (acceptance criterion). A local countdown
 *     disagrees with the API's own cooldown as soon as the tab is backgrounded
 *     or the clock drifts, and then offers a resend that 429s.
 *  4. **Autosave (SIL-1559).** Steps 1 and 2 save their own fields ~800ms after
 *     the last edit, and on Back, close and tab-hide, through
 *     `useBuyerDraftAutosave` (serialized, skips unchanged). Each save response is
 *     written into the status cache so reopening resumes from it. The step-3
 *     phone number is component state only - never persisted server-side.
 *
 * Error codes are never translated here: every message goes through
 * `exchangeErrorMessage` (./errors), and every sentence through
 * `./copy/buyerVerification`.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { track } from '@siliconx/shared/analytics';
import {
  ENTITY_TYPES,
  PURCHASE_VOLUMES,
  guessDefaultDialCountry,
  isAllowedTwilioVerifySmsPhone,
  phoneValidationMessage,
  reformatPhoneWithCaret,
  toE164,
  type BuyerVerificationDraft,
  type BuyerVerificationResponse,
  type CountryCode,
} from '@siliconx/shared';
import { Card } from '@siliconx/shared/ui/Card';
import { Button } from '@siliconx/shared/ui/Button';
import { Field, TextInput } from '@siliconx/shared/ui/Field';
import { Select } from '@siliconx/shared/ui/Select';
import { PhoneField } from '../PhoneField';
import { useBuyerStatus, useInvalidateBuyerStatus, useSetBuyerStatus } from '../../hooks/useBuyerStatus';
import { useBuyerDraftAutosave } from './useBuyerDraftAutosave';
import { exchangeErrorMessage, verificationIncompleteMissing } from './errors';
import {
  S3UploadError,
  reacceptExchangeUa,
  rejectDocument,
  saveBuyerVerificationDraft,
  sendBuyerVerificationCode,
  submitBuyerVerification,
  uploadBuyerVerificationDocument,
  verifyBuyerVerificationCode,
} from './buyerVerificationApi';
import {
  BUYER_VERIFICATION_ADDRESS_LABEL,
  BUYER_VERIFICATION_AUTOSAVE_ERROR,
  BUYER_VERIFICATION_AUTOSAVE_SAVED,
  BUYER_VERIFICATION_AUTOSAVE_SAVING,
  BUYER_VERIFICATION_AUTHORITY_CHECKBOX,
  BUYER_VERIFICATION_BACK,
  BUYER_VERIFICATION_CANCEL,
  BUYER_VERIFICATION_CODE_LABEL,
  BUYER_VERIFICATION_CODE_SENT,
  BUYER_VERIFICATION_CONTINUE,
  BUYER_VERIFICATION_COUNTRY_HINT,
  BUYER_VERIFICATION_COUNTRY_LABEL,
  BUYER_VERIFICATION_DOCUMENT_ATTACH,
  BUYER_VERIFICATION_DOCUMENT_ATTACHED,
  BUYER_VERIFICATION_DOCUMENT_HINT,
  BUYER_VERIFICATION_DOCUMENT_LABEL,
  BUYER_VERIFICATION_DOCUMENT_REPLACE,
  BUYER_VERIFICATION_DOCUMENT_SIZE_ERROR,
  BUYER_VERIFICATION_DOCUMENT_TYPE_ERROR,
  BUYER_VERIFICATION_DOCUMENT_UPLOADING,
  BUYER_VERIFICATION_DOCUMENT_UPLOAD_FAILED,
  BUYER_VERIFICATION_ENTITY_TYPE_LABEL,
  BUYER_VERIFICATION_ENTITY_TYPE_LABELS,
  BUYER_VERIFICATION_LEGAL_NAME_LABEL,
  BUYER_VERIFICATION_MEMBER_NOTE,
  BUYER_VERIFICATION_MODAL_SUBTITLE,
  BUYER_VERIFICATION_MODAL_TITLE,
  BUYER_VERIFICATION_OWNERS_HINT,
  BUYER_VERIFICATION_OWNERS_LABEL,
  BUYER_VERIFICATION_PENDING_NOTE,
  BUYER_VERIFICATION_PHONE_CONSENT,
  BUYER_VERIFICATION_PHONE_HINT,
  BUYER_VERIFICATION_PHONE_LABEL,
  BUYER_VERIFICATION_PHONE_NOT_SUPPORTED,
  BUYER_VERIFICATION_PRIVACY_LINK_LABEL,
  BUYER_VERIFICATION_PRIVACY_ROUTE,
  BUYER_VERIFICATION_TERMS_LINK_LABEL,
  BUYER_VERIFICATION_TERMS_ROUTE,
  BUYER_VERIFICATION_PRIMARY_USE_LABEL,
  BUYER_VERIFICATION_REGISTRATION_NUMBER_HINT,
  BUYER_VERIFICATION_REGISTRATION_NUMBER_LABEL,
  BUYER_VERIFICATION_REJECTED_HEADING,
  BUYER_VERIFICATION_REJECTED_NO_REASON,
  BUYER_VERIFICATION_RESEND,
  BUYER_VERIFICATION_REVIEW_HEADING,
  BUYER_VERIFICATION_REVIEW_MOBILE_LABEL,
  BUYER_VERIFICATION_REVIEW_MOBILE_VERIFIED,
  BUYER_VERIFICATION_REVIEW_NOT_PROVIDED,
  BUYER_VERIFICATION_SAVING,
  BUYER_VERIFICATION_SCREENING_NOTE,
  BUYER_VERIFICATION_SENDING_CODE,
  BUYER_VERIFICATION_SEND_CODE,
  BUYER_VERIFICATION_SIGNATORY_NAME_HINT,
  BUYER_VERIFICATION_SIGNATORY_NAME_LABEL,
  BUYER_VERIFICATION_SIGNATORY_TITLE_LABEL,
  BUYER_VERIFICATION_STATUS_LABELS,
  BUYER_VERIFICATION_STATUS_PREFIX,
  BUYER_VERIFICATION_STEPS,
  BUYER_VERIFICATION_SUBMIT,
  BUYER_VERIFICATION_SUBMITTED_TOAST_BODY,
  BUYER_VERIFICATION_SUBMITTED_TOAST_TITLE,
  BUYER_VERIFICATION_SUBMITTING,
  BUYER_VERIFICATION_TRY_AGAIN,
  BUYER_VERIFICATION_UA_CHECKBOX,
  BUYER_VERIFICATION_UA_LINK_LABEL,
  BUYER_VERIFICATION_UA_ROUTE,
  BUYER_VERIFICATION_REACCEPTANCE_HEADING,
  BUYER_VERIFICATION_REACCEPTANCE_BODY,
  BUYER_VERIFICATION_REACCEPTANCE_ACCEPT,
  BUYER_VERIFICATION_REACCEPTANCE_ACCEPTING,
  BUYER_VERIFICATION_VERIFIED_NOTE,
  BUYER_VERIFICATION_VERIFY,
  BUYER_VERIFICATION_VERIFYING,
  BUYER_VERIFICATION_VOLUME_LABEL,
  BUYER_VERIFICATION_VOLUME_LABELS,
  BUYER_VERIFICATION_WEBSITE_LABEL,
  buyerVerificationCodeSentHint,
  buyerVerificationIdentityLine,
  buyerVerificationPhoneVerified,
  buyerVerificationResendIn,
  buyerVerificationUaVersionNote,
} from '@siliconx/shared/ui/buyer-listing/copy/buyerVerification';

export interface BuyerVerificationModalProps {
  /**
   * Info callout shown above the form, set when a gated action opened the
   * modal (`BUYER_GATE_CALLOUT`); `null`/omitted when the user opened it
   * themselves from a "Get verified" affordance.
   */
  reason?: string | null;
  onClose: () => void;
}

type Draft = NonNullable<BuyerVerificationResponse['draft']>;
type Step = 1 | 2 | 3 | 4;
type Busy = 'draft' | 'upload' | 'send' | 'verify' | 'submit' | 'reaccept' | null;

interface FormValues {
  legalName: string;
  entityType: string;
  country: string;
  registrationNumber: string;
  website: string;
  registeredAddress: string;
  signatoryName: string;
  signatoryTitle: string;
  beneficialOwners: string;
  purchaseVolume: string;
  primaryUse: string;
  authorityConfirmed: boolean;
}

const EMPTY_VALUES: FormValues = {
  legalName: '',
  entityType: '',
  country: '',
  registrationNumber: '',
  website: '',
  registeredAddress: '',
  signatoryName: '',
  signatoryTitle: '',
  beneficialOwners: '',
  purchaseVolume: '',
  primaryUse: '',
  authorityConfirmed: false,
};

function valuesFromDraft(draft: Draft): FormValues {
  return {
    legalName: draft.legalName ?? '',
    entityType: draft.entityType ?? '',
    country: draft.country ?? '',
    registrationNumber: draft.registrationNumber ?? '',
    website: draft.website ?? '',
    registeredAddress: draft.registeredAddress ?? '',
    signatoryName: draft.signatoryName ?? '',
    signatoryTitle: draft.signatoryTitle ?? '',
    beneficialOwners: draft.beneficialOwners ?? '',
    purchaseVolume: draft.purchaseVolume ?? '',
    primaryUse: draft.primaryUse ?? '',
    authorityConfirmed: draft.authorityConfirmed ?? false,
  };
}

/** Narrow a free-text select value to the DTO's enum, or drop it from the patch. */
function asEnum<T extends readonly string[]>(options: T, raw: string): T[number] | undefined {
  return (options as readonly string[]).includes(raw) ? (raw as T[number]) : undefined;
}

/**
 * Step 1 and step 2 send only their own fields. Text fields are always
 * included (so clearing one persists); `country` and the two enums are omitted
 * when empty or malformed, because the DTO validates their SHAPE - an empty
 * `country` is a 400, not a cleared field.
 */
function step1Patch(v: FormValues): BuyerVerificationDraft {
  const patch: BuyerVerificationDraft = {
    legalName: v.legalName.trim(),
    registrationNumber: v.registrationNumber.trim(),
    website: v.website.trim(),
    registeredAddress: v.registeredAddress.trim(),
  };
  const entityType = asEnum(ENTITY_TYPES, v.entityType);
  if (entityType) patch.entityType = entityType;
  const country = v.country.trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(country)) patch.country = country;
  return patch;
}

/**
 * Autosave's step-1 patch: everything except `registrationNumber`. The
 * registration / tax id is PII, so it is only ever sent by an explicit Continue
 * (submit needs it) - never by the debounced, flush or retry paths. The typed
 * value still lives in component state, so Back never loses it.
 */
function step1AutosavePatch(v: FormValues): BuyerVerificationDraft {
  const { registrationNumber: _omitted, ...rest } = step1Patch(v);
  return rest;
}

function step2Patch(v: FormValues): BuyerVerificationDraft {
  const patch: BuyerVerificationDraft = {
    signatoryName: v.signatoryName.trim(),
    signatoryTitle: v.signatoryTitle.trim(),
    beneficialOwners: v.beneficialOwners.trim(),
    primaryUse: v.primaryUse.trim(),
    authorityConfirmed: v.authorityConfirmed,
  };
  const purchaseVolume = asEnum(PURCHASE_VOLUMES, v.purchaseVolume);
  if (purchaseVolume) patch.purchaseVolume = purchaseVolume;
  return patch;
}

/**
 * "The step after the last complete one" (spec 4.5). Completeness per step is
 * the handoff's Continue-enable rule, read off the SERVER's draft rather than
 * local state - which is what makes closing the modal mid-flow and reopening
 * land in the right place.
 */
function resumeStep(draft: Draft): Step {
  if (!draft.legalName?.trim() || !draft.registrationNumber?.trim()) return 1;
  if (!draft.signatoryName?.trim() || !draft.documentUploaded || !draft.authorityConfirmed) return 2;
  if (!draft.phoneVerified) return 3;
  return 4;
}

/**
 * `VERIFICATION_INCOMPLETE.missing` carries the camelCase names
 * `submit_buyer_verification` (migration 953) emits. Spec section 6: go to the
 * FIRST step that has a missing field and mark it.
 */
const STEP_OF_FIELD: Record<string, Step> = {
  legalName: 1,
  registrationNumber: 1,
  signatoryName: 2,
  signatoryTitle: 2,
  authorityConfirmed: 2,
  document: 2,
  phone: 3,
  exchangeUa: 4,
};

function firstMissingStep(missing: string[]): Step {
  const steps = missing.map((field) => STEP_OF_FIELD[field]).filter((s): s is Step => s !== undefined);
  return steps.length > 0 ? (Math.min(...steps) as Step) : 1;
}

/**
 * Whole seconds until `iso`, ticking once a second. Returns 0 for a null,
 * malformed or past timestamp, which is what makes "resend available" the
 * default rather than a state the UI can get stuck outside of.
 */
function useSecondsUntil(iso: string | null): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!iso) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [iso]);

  if (!iso) return 0;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return 0;
  return Math.max(0, Math.ceil((target - now) / 1000));
}

/** The masked form the API uses, for the moment before a status refetch lands. */
function localMask(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 4 ? `•••• •••-${digits.slice(-4)}` : '••••';
}

/**
 * SIL-1574: E.164 for `phone`/`country` iff it is both a valid number for
 * that country AND one the API's `isAllowedTwilioVerifySmsPhone` accepts
 * (US/CA mobile, not toll-free/premium) - null otherwise. The send button
 * and `handleSendCode` share this so the client never fires a request the
 * server is certain to reject.
 */
function sendablePhoneE164(phone: string, country: CountryCode): string | null {
  const e164 = toE164(phone, country);
  return e164 && isAllowedTwilioVerifySmsPhone(e164) ? e164 : null;
}

function Callout({ tone, children }: { tone: 'info' | 'error' | 'success'; children: ReactNode }) {
  const styles = {
    info: 'border-brand-200 bg-brand-50 text-brand-900',
    error: 'border-red-200 bg-red-50 text-red-900',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  }[tone];
  return <div className={`rounded-md border p-3 text-sm ${styles}`}>{children}</div>;
}

function TextArea({
  value,
  onChange,
  rows = 3,
}: {
  value: string;
  onChange: (next: string) => void;
  rows?: number;
}) {
  return (
    <textarea
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-border px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
    />
  );
}

function CheckboxRow({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label className="flex items-start gap-2 text-sm text-foreground">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-4 shrink-0 rounded border-border"
      />
      <span>{children}</span>
    </label>
  );
}

function Stepper({ step }: { step: Step }) {
  return (
    <ol className="mt-5 flex flex-wrap gap-x-4 gap-y-1 text-xs" aria-label="Verification steps">
      {BUYER_VERIFICATION_STEPS.map((label, index) => {
        const number = index + 1;
        const state = number === step ? 'current' : number < step ? 'complete' : 'upcoming';
        return (
          <li
            key={label}
            aria-current={state === 'current' ? 'step' : undefined}
            className={
              state === 'current'
                ? 'font-semibold text-brand-700'
                : state === 'complete'
                  ? 'text-foreground'
                  : 'text-muted-foreground'
            }
          >
            <span className="font-num">{number}</span> {label}
          </li>
        );
      })}
    </ol>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3 border-b border-border/60 py-1.5 last:border-0">
      <dt className="w-44 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-foreground">{value || BUYER_VERIFICATION_REVIEW_NOT_PROVIDED}</dd>
    </div>
  );
}

export function BuyerVerificationModal({ reason, onClose }: BuyerVerificationModalProps) {
  const titleId = useId();
  const { data } = useBuyerStatus();
  const invalidateBuyerStatus = useInvalidateBuyerStatus();
  const setBuyerStatus = useSetBuyerStatus();

  const [step, setStep] = useState<Step>(1);
  const [values, setValues] = useState<FormValues>(EMPTY_VALUES);
  const [documentUploaded, setDocumentUploaded] = useState(false);
  const [documentName, setDocumentName] = useState<string | null>(null);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  // SIL-1574: dial country for the SIL-1198 PhoneField; defaulted off the
  // entity's own country (values.country) once seeded, 'US' until then.
  const [phoneCountry, setPhoneCountry] = useState<CountryCode>('US');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [phoneMasked, setPhoneMasked] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [resendAvailableAt, setResendAvailableAt] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [uaAccepted, setUaAccepted] = useState(false);
  const [busy, setBusy] = useState<Busy>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const [missing, setMissing] = useState<string[]>([]);
  const [ready, setReady] = useState(false);
  // Captured at seed time: the first autosave flips the status to 'draft', and
  // the reviewer's reason must not vanish while the user is fixing the form.
  const [rejection, setRejection] = useState<{ reason: string | null } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const seeded = useRef(false);

  const resendIn = useSecondsUntil(resendAvailableAt);

  const autosave = useBuyerDraftAutosave({
    save: saveBuyerVerificationDraft,
    onSaved: setBuyerStatus,
    enabled: ready && data?.canEdit === true && data.draft != null,
  });
  const { flush: flushDraft, markSaved, schedule: scheduleDraft } = autosave;

  // Every exit (X, overlay, Cancel, Escape) persists pending input first.
  const requestClose = useCallback(() => {
    void flushDraft();
    onClose();
  }, [flushDraft, onClose]);

  // Escape closes - the overlay click and Cancel are pointer-only exits.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [requestClose]);

  const applyDraft = useCallback(
    (draft: Draft, status: BuyerVerificationResponse['status'], rejectionReason: string | null) => {
      const seededValues = valuesFromDraft(draft);
      // The server's own values are the "already saved" baseline, so merely
      // opening the modal (or editing a field back) never sends a request.
      markSaved('step1', step1AutosavePatch(seededValues));
    markSaved('step1-full', step1Patch(seededValues));
      markSaved('step2', step2Patch(seededValues));
      setRejection(status === 'rejected' ? { reason: rejectionReason } : null);
      setReady(true);
      setValues(seededValues);
      setDocumentUploaded(draft.documentUploaded);
      setPhoneVerified(draft.phoneVerified);
      setPhoneMasked(draft.phoneMasked);
      // SIL-1574: the phone number itself is never persisted server-side (see
      // note 4 in the file header), so only the dial country can be seeded -
      // off the entity's own country field.
      setPhoneCountry(guessDefaultDialCountry({ addressCountry: seededValues.country }));
      // A rejected record always opens on step 1: the reviewer's reason is about
      // the entity, and resuming on "Agreement and review" hides it behind three
      // steps the user has no reason to revisit blind.
      setStep(status === 'rejected' ? 1 : resumeStep(draft));
    },
    [markSaved],
  );

  // SIL-1559: debounced autosave of the CURRENT step's fields.
  useEffect(() => {
    if (!ready) return;
    if (step === 1) scheduleDraft('step1', step1AutosavePatch(values));
    else if (step === 2) scheduleDraft('step2', step2Patch(values));
  }, [ready, step, values, scheduleDraft]);

  useEffect(() => {
    if (seeded.current || !data || data.draft == null) return;
    seeded.current = true;
    const draft = data.draft;

    // See note 1 in the file header: an unseeded record has no prefill until a
    // write runs `ensureDraft`. Only pay for it when the prefill is actually
    // absent, and fall back to the unseeded draft if the write fails - a
    // network blip must not leave the form blank AND unusable.
    if (data.canEdit && !draft.legalName && !draft.signatoryName) {
      void saveBuyerVerificationDraft({})
        .then((seededResponse) =>
          applyDraft(seededResponse.draft ?? draft, seededResponse.status, data.rejectionReason),
        )
        .catch(() => applyDraft(draft, data.status, data.rejectionReason));
      return;
    }
    applyDraft(draft, data.status, data.rejectionReason);
  }, [data, applyDraft]);

  const canContinue = useMemo(() => {
    switch (step) {
      // Handoff section 5, verbatim: "name and registration number are non-empty".
      case 1:
        return values.legalName.trim() !== '' && values.registrationNumber.trim() !== '';
      // "signatory, document and authority checkbox".
      case 2:
        return values.signatoryName.trim() !== '' && documentUploaded && values.authorityConfirmed;
      // "the number is SMS-verified".
      case 3:
        return phoneVerified;
      // The prototype's "NDA checked", now the Exchange User Agreement.
      case 4:
        return uaAccepted;
    }
  }, [step, values, documentUploaded, phoneVerified, uaAccepted]);

  const handleFile = useCallback(
    async (file: File) => {
      const rejection = rejectDocument(file);
      if (rejection) {
        setDocumentError(
          rejection === 'type' ? BUYER_VERIFICATION_DOCUMENT_TYPE_ERROR : BUYER_VERIFICATION_DOCUMENT_SIZE_ERROR,
        );
        return;
      }
      setDocumentError(null);
      setBusy('upload');
      try {
        const response = await uploadBuyerVerificationDocument(file);
        setDocumentUploaded(response.draft?.documentUploaded ?? true);
        setDocumentName(file.name);
        await invalidateBuyerStatus();
      } catch (e) {
        setDocumentError(
          e instanceof S3UploadError
            ? BUYER_VERIFICATION_DOCUMENT_UPLOAD_FAILED
            : exchangeErrorMessage(e, BUYER_VERIFICATION_DOCUMENT_UPLOAD_FAILED),
        );
      } finally {
        setBusy(null);
      }
    },
    [invalidateBuyerStatus],
  );

  // SIL-1574: dial-code reformat on country switch, mirroring individual-
  // profile.tsx's handlePhoneCountryChange - re-runs AsYouType against the
  // digits already typed so the display stays formatted for the new country.
  const handlePhoneCountryChange = useCallback((next: CountryCode) => {
    setPhoneCountry(next);
    setPhoneError(null);
    setPhone((prev) => (prev.trim() ? reformatPhoneWithCaret(prev, prev.length, next).formatted : prev));
  }, []);

  const handlePhoneBlur = useCallback(() => {
    const trimmed = phone.trim();
    if (!trimmed) {
      setPhoneError(null);
      return;
    }
    const message = phoneValidationMessage(trimmed, phoneCountry);
    if (message) {
      setPhoneError(message);
      return;
    }
    setPhoneError(sendablePhoneE164(trimmed, phoneCountry) ? null : BUYER_VERIFICATION_PHONE_NOT_SUPPORTED);
  }, [phone, phoneCountry]);

  const handleSendCode = useCallback(async () => {
    setOtpError(null);
    const trimmed = phone.trim();
    const e164 = sendablePhoneE164(trimmed, phoneCountry);
    if (!e164) {
      setPhoneError(phoneValidationMessage(trimmed, phoneCountry) ?? BUYER_VERIFICATION_PHONE_NOT_SUPPORTED);
      return;
    }
    setBusy('send');
    try {
      const { resendAvailableAt: nextResend } = await sendBuyerVerificationCode(e164);
      setResendAvailableAt(nextResend ?? null);
      setPhoneMasked(localMask(phone));
      setCodeSent(true);
      setCode('');
      await invalidateBuyerStatus();
    } catch (e) {
      setOtpError(exchangeErrorMessage(e));
    } finally {
      setBusy(null);
    }
  }, [phone, phoneCountry, invalidateBuyerStatus]);

  const handleVerifyCode = useCallback(async () => {
    setOtpError(null);
    setBusy('verify');
    try {
      await verifyBuyerVerificationCode(code);
      setPhoneVerified(true);
      await invalidateBuyerStatus();
    } catch (e) {
      setOtpError(exchangeErrorMessage(e));
    } finally {
      setBusy(null);
    }
  }, [code, invalidateBuyerStatus]);

  const handleSubmit = useCallback(async () => {
    setStepError(null);
    setMissing([]);
    setBusy('submit');
    try {
      await submitBuyerVerification();
      track('verification_submit', { result: 'success' });
      toast.success(BUYER_VERIFICATION_SUBMITTED_TOAST_TITLE, {
        description: BUYER_VERIFICATION_SUBMITTED_TOAST_BODY,
      });
      // Approval arrives later as a notification (slice 1) and the page reacts
      // on the NEXT status fetch - deliberately no polling here.
      await invalidateBuyerStatus();
      onClose();
    } catch (e) {
      const incomplete = verificationIncompleteMissing(e);
      if (incomplete.length > 0) {
        track('verification_submit', { result: 'incomplete' });
        track('form_error', { form: 'verification', reason: 'validation', error_field: String(incomplete[0]) });
        setMissing(incomplete);
        setStep(firstMissingStep(incomplete));
      } else {
        track('form_error', { form: 'verification', reason: 'api' });
      }
      setStepError(exchangeErrorMessage(e));
      setBusy(null);
    }
  }, [invalidateBuyerStatus, onClose]);

  /**
   * SIL-1359 — the agreement-only path. Deliberately calls neither
   * `submitBuyerVerification` (that moves status to 'pending' — wrong here,
   * the org stays 'verified' the whole time) nor anything that touches KYB
   * fields; `reacceptExchangeUa` hits `POST /agreement/accept` only.
   */
  const [reacceptError, setReacceptError] = useState<string | null>(null);
  const handleReaccept = useCallback(async () => {
    setReacceptError(null);
    setBusy('reaccept');
    try {
      await reacceptExchangeUa();
      await invalidateBuyerStatus();
      onClose();
    } catch (e) {
      setReacceptError(exchangeErrorMessage(e));
      setBusy(null);
    }
  }, [invalidateBuyerStatus, onClose]);

  const handlePrimary = useCallback(async () => {
    if (step === 4) {
      await handleSubmit();
      return;
    }
    if (step === 3) {
      setStep(4);
      return;
    }
    setStepError(null);
    setBusy('draft');
    try {
      // Same serialized path as the autosave: waits out any in-flight save and
      // skips the request when the step is already persisted.
      // Explicit save: step 1 includes registrationNumber, which autosave omits.
      scheduleDraft(
        step === 1 ? 'step1-full' : 'step2',
        step === 1 ? step1Patch(values) : step2Patch(values),
        { explicit: true },
      );
      if (await flushDraft()) {
        setStep((step + 1) as Step);
      } else {
        // Spec section 6: stay on the step, inline error, keep the entered values.
        setStepError(exchangeErrorMessage(autosave.lastError()));
      }
    } finally {
      setBusy(null);
    }
  }, [step, values, handleSubmit, scheduleDraft, flushDraft, autosave]);

  const handleBack = useCallback(() => {
    if (step === 1) {
      requestClose();
      return;
    }
    void flushDraft();
    setStep((step - 1) as Step);
  }, [step, requestClose, flushDraft]);

  const set = useCallback(
    <K extends keyof FormValues>(key: K, value: FormValues[K]) =>
      setValues((previous) => ({ ...previous, [key]: value })),
    [],
  );

  const markedMissing = useCallback(
    (field: string) => (missing.includes(field) ? BUYER_VERIFICATION_REVIEW_NOT_PROVIDED : undefined),
    [missing],
  );

  const status = data?.status;
  const draft = data?.draft ?? null;
  const canEdit = data?.canEdit === true;
  // The API nulls `draft` for anyone who is not an org admin (security review
  // #15), so a null draft on a loaded response IS the member case. Checked
  // before `canEdit` so a member never reaches the form under any status.
  const isMember = data != null && draft == null;

  const shell = (body: ReactNode, footer: ReactNode) => (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4"
      onClick={requestClose}
    >
      <Card
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="max-h-full w-full max-w-[600px] overflow-y-auto shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="text-base font-semibold text-foreground">
          {BUYER_VERIFICATION_MODAL_TITLE}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">{BUYER_VERIFICATION_MODAL_SUBTITLE}</p>
        {data?.email ? (
          <p className="mt-2 text-xs text-muted-foreground">
            {buyerVerificationIdentityLine(data.email, data.accountCreatedAt)}
          </p>
        ) : null}

        {reason ? (
          <div className="mt-4">
            <Callout tone="info">{reason}</Callout>
          </div>
        ) : null}

        {body}

        <div className="mt-6 flex justify-between gap-2">{footer}</div>
      </Card>
    </div>
  );

  if (!data) {
    return shell(
      <p className="mt-4 text-xs text-muted-foreground">Loading…</p>,
      <>
        <span />
        <Button variant="secondary" onClick={requestClose}>
          {BUYER_VERIFICATION_CANCEL}
        </Button>
      </>,
    );
  }

  const statusLine = (
    <p className="mt-4 text-sm text-foreground">
      <span className="text-muted-foreground">{BUYER_VERIFICATION_STATUS_PREFIX}: </span>
      {BUYER_VERIFICATION_STATUS_LABELS[status ?? ''] ?? status}
    </p>
  );

  const closeOnlyFooter = (
    <>
      <span />
      <Button variant="secondary" onClick={requestClose}>
        {BUYER_VERIFICATION_CANCEL}
      </Button>
    </>
  );

  if (isMember) {
    return shell(
      <>
        {statusLine}
        <p className="mt-2 text-sm text-muted-foreground">{BUYER_VERIFICATION_MEMBER_NOTE}</p>
      </>,
      closeOnlyFooter,
    );
  }

  // SIL-1359: verified, but the accepted agreement version is stale (and not
  // the demo-seed exemption) — agreement-only step, never the 4-step wizard.
  // Checked before the generic `!canEdit` branch below (canEdit is false for
  // every 'verified' record, including this one).
  if (data.needsAgreementReacceptance) {
    return shell(
      <>
        {statusLine}
        <p className="mt-3 text-sm font-medium text-foreground">
          {BUYER_VERIFICATION_REACCEPTANCE_HEADING}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">{BUYER_VERIFICATION_REACCEPTANCE_BODY}</p>
        <div className="mt-4">
          <CheckboxRow checked={uaAccepted} onChange={setUaAccepted}>
            {BUYER_VERIFICATION_UA_CHECKBOX}
          </CheckboxRow>
          <p className="mt-1 text-xs">
            <a
              href={BUYER_VERIFICATION_UA_ROUTE}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-brand-600 hover:underline"
            >
              {BUYER_VERIFICATION_UA_LINK_LABEL}
            </a>
            <span className="text-muted-foreground"> · {buyerVerificationUaVersionNote(data.exchangeUaVersion)}</span>
          </p>
        </div>
        {reacceptError ? <p className="mt-2 text-xs text-red-600">{reacceptError}</p> : null}
      </>,
      <>
        <Button variant="secondary" onClick={requestClose}>
          {BUYER_VERIFICATION_CANCEL}
        </Button>
        <Button disabled={!uaAccepted || busy !== null} onClick={() => void handleReaccept()}>
          {busy === 'reaccept' ? BUYER_VERIFICATION_REACCEPTANCE_ACCEPTING : BUYER_VERIFICATION_REACCEPTANCE_ACCEPT}
        </Button>
      </>,
    );
  }

  if (!canEdit) {
    return shell(
      <>
        {statusLine}
        <p className="mt-2 text-sm text-muted-foreground">
          {status === 'verified' ? BUYER_VERIFICATION_VERIFIED_NOTE : BUYER_VERIFICATION_PENDING_NOTE}
        </p>
      </>,
      closeOnlyFooter,
    );
  }

  const body = (
    <>
      {rejection ? (
        <div className="mt-4">
          <Callout tone="error">
            <span className="font-medium">{BUYER_VERIFICATION_REJECTED_HEADING}</span>
            <span className="mt-1 block">
              {rejection.reason ?? BUYER_VERIFICATION_REJECTED_NO_REASON}
            </span>
          </Callout>
        </div>
      ) : null}

      <Stepper step={step} />

      <div className="mt-5 space-y-4">
        {step === 1 ? (
          <>
            <Field label={BUYER_VERIFICATION_LEGAL_NAME_LABEL} error={markedMissing('legalName')}>
              <TextInput value={values.legalName} onChange={(e) => set('legalName', e.target.value)} />
            </Field>
            <Field label={BUYER_VERIFICATION_ENTITY_TYPE_LABEL}>
              <Select value={values.entityType} onChange={(e) => set('entityType', e.target.value)}>
                <option value="">{BUYER_VERIFICATION_REVIEW_NOT_PROVIDED}</option>
                {ENTITY_TYPES.map((option) => (
                  <option key={option} value={option}>
                    {BUYER_VERIFICATION_ENTITY_TYPE_LABELS[option]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={BUYER_VERIFICATION_COUNTRY_LABEL} hint={BUYER_VERIFICATION_COUNTRY_HINT}>
              <TextInput
                value={values.country}
                maxLength={2}
                onChange={(e) => set('country', e.target.value.toUpperCase())}
              />
            </Field>
            <Field
              label={BUYER_VERIFICATION_REGISTRATION_NUMBER_LABEL}
              hint={BUYER_VERIFICATION_REGISTRATION_NUMBER_HINT}
              error={markedMissing('registrationNumber')}
            >
              <TextInput
                mono
                value={values.registrationNumber}
                onChange={(e) => set('registrationNumber', e.target.value)}
              />
            </Field>
            <Field label={BUYER_VERIFICATION_WEBSITE_LABEL}>
              <TextInput value={values.website} onChange={(e) => set('website', e.target.value)} />
            </Field>
            <div>
              <span className="mb-1 block text-xs font-medium text-muted-foreground">
                {BUYER_VERIFICATION_ADDRESS_LABEL}
              </span>
              <TextArea value={values.registeredAddress} onChange={(next) => set('registeredAddress', next)} />
            </div>
          </>
        ) : null}

        {step === 2 ? (
          <>
            <Field
              label={BUYER_VERIFICATION_SIGNATORY_NAME_LABEL}
              hint={BUYER_VERIFICATION_SIGNATORY_NAME_HINT}
              error={markedMissing('signatoryName')}
            >
              <TextInput value={values.signatoryName} onChange={(e) => set('signatoryName', e.target.value)} />
            </Field>
            <Field label={BUYER_VERIFICATION_SIGNATORY_TITLE_LABEL} error={markedMissing('signatoryTitle')}>
              <TextInput value={values.signatoryTitle} onChange={(e) => set('signatoryTitle', e.target.value)} />
            </Field>
            <div>
              <span className="mb-1 block text-xs font-medium text-muted-foreground">
                {BUYER_VERIFICATION_OWNERS_LABEL}
              </span>
              <TextArea value={values.beneficialOwners} onChange={(next) => set('beneficialOwners', next)} />
              <p className="mt-1 text-xs text-muted-foreground">{BUYER_VERIFICATION_OWNERS_HINT}</p>
            </div>
            <Field label={BUYER_VERIFICATION_VOLUME_LABEL}>
              <Select value={values.purchaseVolume} onChange={(e) => set('purchaseVolume', e.target.value)}>
                <option value="">{BUYER_VERIFICATION_REVIEW_NOT_PROVIDED}</option>
                {PURCHASE_VOLUMES.map((option) => (
                  <option key={option} value={option}>
                    {BUYER_VERIFICATION_VOLUME_LABELS[option]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={BUYER_VERIFICATION_PRIMARY_USE_LABEL}>
              <TextInput value={values.primaryUse} onChange={(e) => set('primaryUse', e.target.value)} />
            </Field>

            <div>
              <span className="mb-1 block text-xs font-medium text-muted-foreground">
                {BUYER_VERIFICATION_DOCUMENT_LABEL}
              </span>
              <div className="flex items-center justify-between gap-3 rounded-md border border-dashed border-border p-3">
                <span className="min-w-0 truncate text-sm text-foreground">
                  {busy === 'upload'
                    ? BUYER_VERIFICATION_DOCUMENT_UPLOADING
                    : documentName ?? (documentUploaded ? BUYER_VERIFICATION_DOCUMENT_ATTACHED : '')}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy !== null}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {documentUploaded ? BUYER_VERIFICATION_DOCUMENT_REPLACE : BUYER_VERIFICATION_DOCUMENT_ATTACH}
                </Button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                aria-label={BUYER_VERIFICATION_DOCUMENT_LABEL}
                accept="application/pdf,image/png,image/jpeg"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) void handleFile(file);
                }}
              />
              {documentError ? (
                <p className="mt-1 text-xs text-red-600">
                  {documentError}{' '}
                  <button
                    type="button"
                    className="font-medium underline"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {BUYER_VERIFICATION_TRY_AGAIN}
                  </button>
                </p>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">{BUYER_VERIFICATION_DOCUMENT_HINT}</p>
              )}
              {missing.includes('document') ? (
                <p className="mt-1 text-xs text-red-600">{BUYER_VERIFICATION_REVIEW_NOT_PROVIDED}</p>
              ) : null}
            </div>

            <CheckboxRow
              checked={values.authorityConfirmed}
              onChange={(next) => set('authorityConfirmed', next)}
            >
              {BUYER_VERIFICATION_AUTHORITY_CHECKBOX}
            </CheckboxRow>
          </>
        ) : null}

        {step === 3 ? (
          <>
            {phoneVerified ? (
              // Handoff section 5: the number input locks once verified. The
              // masked value ("•••• •••-1234") isn't a real phone number, so
              // it goes through the plain TextInput rather than PhoneField
              // (whose as-you-type formatting expects real digits).
              <Field label={BUYER_VERIFICATION_PHONE_LABEL}>
                <TextInput mono type="tel" autoComplete="tel" disabled value={phoneMasked ?? ''} />
              </Field>
            ) : (
              <div>
                <label
                  htmlFor="field-phone"
                  className="mb-1 block text-xs font-medium text-muted-foreground"
                >
                  {BUYER_VERIFICATION_PHONE_LABEL}
                </label>
                {/* SIL-1574: the SIL-1198 PhoneField - dial-code selector +
                    as-you-type national formatting - sends E.164 on submit;
                    `hideHeader` skips its own "Phone" label/status-marker row
                    since this label above already covers it. */}
                <PhoneField
                  value={phone}
                  country={phoneCountry}
                  onCountryChange={handlePhoneCountryChange}
                  onChange={setPhone}
                  onBlur={handlePhoneBlur}
                  invalid={Boolean(phoneError) || missing.includes('phone')}
                  errorMessage={phoneError ?? markedMissing('phone')}
                  hideHeader
                />
                {!phoneError && !missing.includes('phone') ? (
                  <p className="mt-1 text-xs text-muted-foreground">{BUYER_VERIFICATION_PHONE_HINT}</p>
                ) : null}
              </div>
            )}

            {phoneVerified ? (
              <Callout tone="success">{buyerVerificationPhoneVerified(phoneMasked ?? '')}</Callout>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">
                  {BUYER_VERIFICATION_PHONE_CONSENT}{' '}
                  <a
                    href={BUYER_VERIFICATION_TERMS_ROUTE}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-brand-600 hover:underline"
                  >
                    {BUYER_VERIFICATION_TERMS_LINK_LABEL}
                  </a>{' '}
                  <a
                    href={BUYER_VERIFICATION_PRIVACY_ROUTE}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-brand-600 hover:underline"
                  >
                    {BUYER_VERIFICATION_PRIVACY_LINK_LABEL}
                  </a>
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant={codeSent ? 'secondary' : 'primary'}
                    disabled={
                      busy !== null || !sendablePhoneE164(phone.trim(), phoneCountry) || (codeSent && resendIn > 0)
                    }
                    onClick={() => void handleSendCode()}
                  >
                    {busy === 'send'
                      ? BUYER_VERIFICATION_SENDING_CODE
                      : codeSent
                        ? resendIn > 0
                          ? buyerVerificationResendIn(resendIn)
                          : BUYER_VERIFICATION_RESEND
                        : BUYER_VERIFICATION_SEND_CODE}
                  </Button>
                  {codeSent ? (
                    <span className="text-xs font-medium text-emerald-700">{BUYER_VERIFICATION_CODE_SENT}</span>
                  ) : null}
                </div>

                {codeSent ? (
                  <>
                    <p className="text-xs text-muted-foreground">
                      {buyerVerificationCodeSentHint(phoneMasked ?? localMask(phone))}
                    </p>
                    <div className="flex items-end gap-2">
                      <Field label={BUYER_VERIFICATION_CODE_LABEL} className="flex-1">
                        <TextInput
                          mono
                          inputMode="numeric"
                          maxLength={6}
                          value={code}
                          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        />
                      </Field>
                      <Button
                        size="sm"
                        disabled={busy !== null || code.length !== 6}
                        onClick={() => void handleVerifyCode()}
                      >
                        {busy === 'verify' ? BUYER_VERIFICATION_VERIFYING : BUYER_VERIFICATION_VERIFY}
                      </Button>
                    </div>
                  </>
                ) : null}
              </>
            )}

            {otpError ? <p className="text-xs text-red-600">{otpError}</p> : null}
          </>
        ) : null}

        {step === 4 ? (
          <>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {BUYER_VERIFICATION_REVIEW_HEADING}
            </h3>
            <dl className="text-xs">
              <ReviewRow label={BUYER_VERIFICATION_LEGAL_NAME_LABEL} value={values.legalName} />
              <ReviewRow
                label={BUYER_VERIFICATION_ENTITY_TYPE_LABEL}
                value={BUYER_VERIFICATION_ENTITY_TYPE_LABELS[values.entityType] ?? ''}
              />
              <ReviewRow label={BUYER_VERIFICATION_COUNTRY_LABEL} value={values.country} />
              <ReviewRow
                label={BUYER_VERIFICATION_REGISTRATION_NUMBER_LABEL}
                value={values.registrationNumber}
              />
              <ReviewRow label={BUYER_VERIFICATION_WEBSITE_LABEL} value={values.website} />
              <ReviewRow label={BUYER_VERIFICATION_ADDRESS_LABEL} value={values.registeredAddress} />
              <ReviewRow label={BUYER_VERIFICATION_SIGNATORY_NAME_LABEL} value={values.signatoryName} />
              <ReviewRow label={BUYER_VERIFICATION_SIGNATORY_TITLE_LABEL} value={values.signatoryTitle} />
              <ReviewRow label={BUYER_VERIFICATION_OWNERS_LABEL} value={values.beneficialOwners} />
              <ReviewRow
                label={BUYER_VERIFICATION_VOLUME_LABEL}
                value={BUYER_VERIFICATION_VOLUME_LABELS[values.purchaseVolume] ?? ''}
              />
              <ReviewRow label={BUYER_VERIFICATION_PRIMARY_USE_LABEL} value={values.primaryUse} />
              <ReviewRow
                label={BUYER_VERIFICATION_DOCUMENT_LABEL}
                value={documentUploaded ? documentName ?? BUYER_VERIFICATION_DOCUMENT_ATTACHED : ''}
              />
              <ReviewRow
                label={BUYER_VERIFICATION_REVIEW_MOBILE_LABEL}
                value={
                  phoneVerified
                    ? `${phoneMasked ?? ''} · ${BUYER_VERIFICATION_REVIEW_MOBILE_VERIFIED}`.trim()
                    : ''
                }
              />
            </dl>

            <p className="text-xs text-muted-foreground">{BUYER_VERIFICATION_SCREENING_NOTE}</p>

            <CheckboxRow checked={uaAccepted} onChange={setUaAccepted}>
              {BUYER_VERIFICATION_UA_CHECKBOX}
            </CheckboxRow>
            <p className="text-xs">
              <a
                href={BUYER_VERIFICATION_UA_ROUTE}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-brand-600 hover:underline"
              >
                {BUYER_VERIFICATION_UA_LINK_LABEL}
              </a>
              <span className="text-muted-foreground">
                {' '}
                · {buyerVerificationUaVersionNote(data.exchangeUaVersion)}
              </span>
            </p>
          </>
        ) : null}

        {stepError ? <p className="text-xs text-red-600">{stepError}</p> : null}
      </div>
    </>
  );

  const primaryLabel =
    step === 4
      ? busy === 'submit'
        ? BUYER_VERIFICATION_SUBMITTING
        : BUYER_VERIFICATION_SUBMIT
      : busy === 'draft'
        ? BUYER_VERIFICATION_SAVING
        : BUYER_VERIFICATION_CONTINUE;

  const footer = (
    <>
      <Button
        variant="secondary"
        disabled={busy !== null}
        onClick={handleBack}
      >
        {step === 1 ? BUYER_VERIFICATION_CANCEL : BUYER_VERIFICATION_BACK}
      </Button>
      <span role="status" aria-live="polite" className="flex-1 self-center text-center text-xs text-muted-foreground">
        {step <= 2 && autosave.status === 'saving' ? BUYER_VERIFICATION_AUTOSAVE_SAVING : null}
        {step <= 2 && autosave.status === 'saved' ? BUYER_VERIFICATION_AUTOSAVE_SAVED : null}
        {step <= 2 && autosave.status === 'error' ? (
          <span className="text-red-600">{BUYER_VERIFICATION_AUTOSAVE_ERROR}</span>
        ) : null}
      </span>
      <Button disabled={!canContinue || busy !== null} onClick={() => void handlePrimary()}>
        {primaryLabel}
      </Button>
    </>
  );

  return shell(body, footer);
}

export default BuyerVerificationModal;
